/**
 * Bewerken van één bibliotheekitem (23-10-2026, Jordy: fase 1, de admin-
 * controls van het oude Content Library-scherm terug).
 *
 * Zelfde velden en zelfde route als oud (`PATCH /items/{id}/process`): alleen
 * wat veranderd is gaat mee. Let op wat de server daarmee doet:
 * - opslaan zet het item op Processed (zo deed oud het ook);
 * - een nieuwe titel staat alleen in het dashboard; het bestand op de NAS
 *   wordt niet hernoemd of aangepast (26-10-2026, Jordy).
 * Een andere map gaat via `PATCH /items/{id}/move`.
 */
import { useMemo, useState } from 'react'
import { Modal, Field } from './Modal'
import { api } from '../lib/api'

export interface BewerkItem {
  id: number
  folder_id: number | null
  title: string
  description?: string | null
  content_type: string
  intensity: string | null
  status?: string | null
  processing_status: string
  base_price?: number | null
  min_price?: number | null
  max_price?: number | null
  tags: string[]
  progression_order?: number | null
}

export interface MapKeuze { id: number; pad: string }

const SOORTEN = ['photo', 'video', 'album', 'audio']
const INTENSITEIT = ['teasing', 'soft', 'medium', 'explicit', 'premium']
const STATUS = ['active', 'paused']

function getal(v: string): number | null {
  const n = Number(String(v).replace(',', '.'))
  return v.trim() === '' || !Number.isFinite(n) ? null : n
}

export function ContentBewerken({ item, mappen, onClose, onKlaar }: {
  item: BewerkItem
  mappen: MapKeuze[]
  onClose: () => void
  onKlaar: (melding: string) => void
}) {
  const [titel, setTitel] = useState(item.title ?? '')
  const [omschrijving, setOmschrijving] = useState(item.description ?? '')
  const [soort, setSoort] = useState(item.content_type || 'photo')
  const [intensiteit, setIntensiteit] = useState(item.intensity || 'soft')
  const [status, setStatus] = useState(STATUS.includes(String(item.status)) ? String(item.status) : 'active')
  const [basis, setBasis] = useState(item.base_price != null ? String(item.base_price) : '')
  const [min, setMin] = useState(item.min_price != null ? String(item.min_price) : '')
  const [max, setMax] = useState(item.max_price != null ? String(item.max_price) : '')
  const [tags, setTags] = useState((item.tags ?? []).join(', '))
  const [volgorde, setVolgorde] = useState(item.progression_order != null ? String(item.progression_order) : '0')
  const [map, setMap] = useState(item.folder_id != null ? String(item.folder_id) : '')
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)

  const verwerkt = item.processing_status === 'processed'

  /** Alleen de velden die echt anders zijn dan wat er stond. */
  const wijziging = useMemo(() => {
    const w: Record<string, unknown> = {}
    if (titel.trim() !== (item.title ?? '')) w.title = titel.trim()
    if (omschrijving !== (item.description ?? '')) w.description = omschrijving
    if (soort !== item.content_type) w.content_type = soort
    if (intensiteit !== (item.intensity || 'soft')) w.intensity = intensiteit
    if (status !== (item.status || 'active')) w.status = status
    const b = getal(basis), mn = getal(min), mx = getal(max)
    if (b !== null && b !== (item.base_price ?? null)) w.base_price = b
    if (mn !== null && mn !== (item.min_price ?? null)) w.min_price = mn
    if (mx !== null && mx !== (item.max_price ?? null)) w.max_price = mx
    const t = tags.split(',').map((s) => s.trim()).filter(Boolean)
    if (t.join('|') !== (item.tags ?? []).join('|')) w.tags = t
    const v = getal(volgorde)
    if (v !== null && v !== (item.progression_order ?? 0)) w.progression_order = Math.round(v)
    return w
  }, [titel, omschrijving, soort, intensiteit, status, basis, min, max, tags, volgorde, item])

  const nieuweMap = map !== '' && Number(map) !== item.folder_id ? Number(map) : null
  const titelAnders = 'title' in wijziging

  async function opslaan() {
    if (!titel.trim()) { setFout('Title cannot be empty.'); return }
    setBezig(true)
    setFout(null)
    try {
      // Een onverwerkt item wordt bij opslaan altijd Processed, ook zonder
      // wijziging: dat is de "Mark as Processed"-knop van oud.
      if (Object.keys(wijziging).length || !verwerkt) {
        await api.patch(`/api/admin/content-library/items/${item.id}/process`, wijziging)
      }
      if (nieuweMap != null) {
        await api.patch(`/api/admin/content-library/items/${item.id}/move`, { folder_id: nieuweMap })
      }
      onKlaar(verwerkt ? 'Changes saved.' : 'Marked as processed.')
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e))
    } finally {
      setBezig(false)
    }
  }

  return (
    <Modal
      title={verwerkt ? 'Edit item' : 'Process item'}
      desc={item.title}
      width={560}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary sm" type="button" disabled={bezig} onClick={opslaan}>
            {bezig ? 'Saving…' : verwerkt ? 'Save changes' : 'Mark as processed'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Title">
            <input className="inp" value={titel} onChange={(e) => setTitel(e.target.value)} />
          </Field>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Description">
            <textarea className="inp" rows={2} value={omschrijving}
                      onChange={(e) => setOmschrijving(e.target.value)} />
          </Field>
        </div>
        <Field label="Content type">
          <select className="inp" value={soort} onChange={(e) => setSoort(e.target.value)}>
            {SOORTEN.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Intensity">
          <select className="inp" value={intensiteit} onChange={(e) => setIntensiteit(e.target.value)}>
            {INTENSITEIT.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select className="inp" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Order">
          <input className="inp" inputMode="numeric" value={volgorde}
                 onChange={(e) => setVolgorde(e.target.value)} />
        </Field>
        <Field label="Base price">
          <input className="inp" inputMode="decimal" value={basis} onChange={(e) => setBasis(e.target.value)} />
        </Field>
        <Field label="Min price">
          <input className="inp" inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} />
        </Field>
        <Field label="Max price">
          <input className="inp" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} />
        </Field>
        <Field label="Folder">
          <select className="inp" value={map} onChange={(e) => setMap(e.target.value)}>
            {item.folder_id == null ? <option value="">—</option> : null}
            {mappen.map((m) => <option key={m.id} value={String(m.id)}>{m.pad}</option>)}
          </select>
        </Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Tags (comma separated)">
            <input className="inp" value={tags} onChange={(e) => setTags(e.target.value)} />
          </Field>
        </div>
      </div>
      {titelAnders ? (
        <div className="note" style={{ marginTop: 10 }}>
          The new title is saved in the dashboard only. The file on the NAS keeps its name.
        </div>
      ) : null}
      {fout ? <div className="note" style={{ marginTop: 10, color: 'var(--bad)' }}>{fout}</div> : null}
    </Modal>
  )
}
